<script setup lang="ts" generic="T extends AObject">
import type { FilterDropdownProps } from "ant-design-vue/es/table/interface";
import { computed, h, nextTick, onBeforeUnmount, onMounted, ref, useSlots, watch } from "vue";
import { renderers } from "./renderers";
import type { CustomRenderParams, CustomTableColumnType, CustomTableProps } from "./types";

const props = withDefaults(defineProps<CustomTableProps<T>>(), {
	showExpandColumn: true,
	showHeader: true,
	showSorterTooltip: true,
	pagination: () => ({}),
});

type CustomFilterDropdownProps = FilterDropdownProps<T>;

const renderRequiredTitle = (title: unknown) => {
	const resolvedTitle = typeof title === "function" ? title() : title;

	return h("span", [h("span", { class: "text-error mr-3" }, "*"), resolvedTitle as never]);
};

const emits = defineEmits<{
	pageSizeChange: [current: number, pageSize: number];
}>();

const slots = useSlots();

const tableRef = ref<HTMLElement | null>(null);
const paginationRef = ref<HTMLElement | null>(null);
const tableHeight = ref(0);
// 记录表格 body 的滚动高度（内容高度）
const tableBodyScrollHeight = ref(0);

let containerResizeObserver: ResizeObserver | null = null;
let updateHeightFrameId: number | null = null;

const scheduleUpdateHeight = () => {
	if (updateHeightFrameId !== null) {
		return;
	}

	updateHeightFrameId = window.requestAnimationFrame(() => {
		updateHeightFrameId = null;
		updateHeight();
	});
};

const updateHeight = async () => {
	await nextTick();

	if (!tableRef.value) {
		return;
	}

	const rect = tableRef.value.getBoundingClientRect();

	if (!rect) {
		return;
	}

	const paginationRect = paginationRef.value?.getBoundingClientRect();

	const tableDom = tableRef.value.querySelector(".ant-table") as HTMLElement;

	if (!tableDom) {
		return;
	}

	const thead = tableDom.querySelector(".ant-table-thead") as HTMLElement;

	const headerHeight = thead?.offsetHeight || 0;
	const paginationHeight = paginationRect?.height || 0;
	const paginationMargin = paginationRect ? 16 : 0;

	// 内容区域可用高度
	const calculatedHeight = rect.height - paginationHeight - paginationMargin - headerHeight;

	// 获取表格内部 body 的实际内容高度（用于判断是否需要滚动）
	const bodyEl =
		(tableDom?.querySelector(".ant-table-body") as HTMLElement | null) ??
		(tableDom?.querySelector("tbody") as HTMLElement | null);

	// 只有当高度有明显变化时才更新，减少不必要的重绘
	if (Math.abs(calculatedHeight - tableHeight.value) > 5) {
		tableHeight.value = calculatedHeight;
	}

	if (bodyEl) {
		tableBodyScrollHeight.value = bodyEl.scrollHeight || 0;
	}
};

const tableScroll = computed(() => {
	// 初始加载阶段，暂时不设置滚动高度，避免闪烁
	if (isInitialLoad.value) {
		return {
			x: "max-content",
		};
	}

	if (!(props.height || tableHeight.value)) {
		return undefined;
	}

	// 如果没有容器高度或 props.height，让 table 自适应
	let height: number;

	if (typeof props.height === "string") {
		height = parseInt(props.height, 10);
	} else {
		height = props.height || 0;
	}

	const limitHeight = height || tableHeight.value;

	return {
		x: "max-content",
		y: limitHeight,
	};
});

// 为表格添加初始样式，防止闪烁
const isInitialLoad = ref(true);

onMounted(() => {
	// 先执行一次高度计算
	scheduleUpdateHeight();

	if (tableRef.value && typeof ResizeObserver !== "undefined") {
		containerResizeObserver = new ResizeObserver(() => {
			scheduleUpdateHeight();
		});

		containerResizeObserver.observe(tableRef.value);
	}

	window.addEventListener("resize", scheduleUpdateHeight);

	// 微任务中标记初始加载完成
	Promise.resolve().then(() => {
		isInitialLoad.value = false;
	});
});

onBeforeUnmount(() => {
	if (updateHeightFrameId !== null) {
		window.cancelAnimationFrame(updateHeightFrameId);
		updateHeightFrameId = null;
	}

	if (containerResizeObserver) {
		containerResizeObserver.disconnect();
		containerResizeObserver = null;
	}

	window.removeEventListener("resize", scheduleUpdateHeight);
});

// 过滤掉 undefined 的 props，只保留实际传入的
const tableProps = computed(() => {
	return Object.fromEntries(
		Object.entries(props).filter(([_, v]) => v !== undefined)
	) as unknown as CustomTableProps;
});

// 监听数据变化，重新计算高度
watch(
	() => props.dataSource,
	() => {
		// 当数据源变化时更新高度，无需过多延迟
		scheduleUpdateHeight();
	},
	{ deep: true }
);

const normalizedColumns = computed(() => {
	if (!props.columns) {
		return [];
	}

	return props.columns.map((col) => {
		if (col.required) {
			return {
				...col,
				title: renderRequiredTitle(col.title),
			};
		}

		const customColumnType = col.customColumn?.type;

		if (customColumnType) {
			return {
				...col,
				customRender: (opt: CustomRenderParams) => {
					return renderers(opt);
				},
			};
		}

		return col;
	});
});

const mergedPagination = computed(() => {
	if (props.pagination === false) {
		return false;
	}

	return {
		showSizeChanger: true,
		showQuickJumper: true,
		showTotal: (total: number) => `共 ${total} 条`,
		pageSizeOptions: ["10", "20", "50", "100"],
		total: 50,
		pageSize: 10,
		current: 1,
		position: "right",
		...props.pagination,
	};
});

const handleChangePageSize = (current: number, pageSize: number) => {
	emits("pageSizeChange", current, pageSize);
};
</script>

<template>
	<div ref="tableRef" class="full custom-table-container flex flex-col">
		<a-table
			v-bind="{
				scroll: tableScroll,
				...tableProps,
				columns: normalizedColumns,
				pagination: false,
			}"
			:class="{ 'table-initializing': isInitialLoad }"
		>
			<!-- 个性化单元格 -->
			<!-- prettier-ignore -->
			<template v-if="slots['bodyCell']" #bodyCell="{ text, record, index, column }">
				<slot
					name="bodyCell"
					:text="text"
					:record="(record as T)"
					:index="index"
					:column="(column as CustomTableColumnType<T>)"
				></slot>
			</template>

			<!-- 自定义筛选菜单，需要配合 column.customFilterDropdown 使用 -->
			<!-- prettier-ignore -->
			<template v-if="slots['customFilterDropdown']" #customFilterDropdown="FilterDropdownProps">
				<slot
					name="customFilterDropdown"
					v-bind="(FilterDropdownProps as CustomFilterDropdownProps)"
				></slot>
			</template>

			<!-- 自定义筛选图标 -->
			<!-- prettier-ignore -->
			<template v-if="slots['customFilterIcon']" #customFilterIcon="{ filtered, column }">
				<slot
					name="customFilterIcon"
					:index="filtered"
					:column="(column as CustomTableColumnType<T>)"
				></slot>
			</template>

			<!-- 自定义空数据时的显示内容 -->
			<template v-if="slots['emptyText']" #emptyText>
				<slot name="emptyText"></slot>
			</template>

			<!-- 额外的展开行 -->
			<!-- prettier-ignore -->
			<template v-if="slots['expandedRowRender']" #expandedRowRender="{ indent, record, index, expanded }">
				<slot
					name="expandedRowRender"
					:indent="indent"
					:record="(record as T)"
					:index="index"
					:expanded="expanded"
				></slot>
			</template>

			<!-- 自定义展开列表头 -->
			<template v-if="slots['expandColumnTitle']" #expandColumnTitle>
				<slot name="expandColumnTitle"></slot>
			</template>

			<!-- 自定义展开图标 -->
			<template v-if="slots['expandIcon']" #expandIcon="slotProps">
				<slot name="expandIcon" v-bind="slotProps"></slot>
			</template>

			<!-- 表格尾部 -->
			<template v-if="slots['footer']" #footer="currentPageData">
				<slot name="footer" v-bind="currentPageData"></slot>
			</template>

			<!-- 个性化头部单元格 -->
			<!-- prettier-ignore -->
			<template v-if="slots['headerCell']" #headerCell="{ title, column }">
				<slot name="headerCell" :title="title" :column="(column as CustomTableColumnType<T>)"></slot>
			</template>

			<!-- 总结栏 -->
			<template v-if="slots['summary']" #summary>
				<slot name="summary"></slot>
			</template>

			<!-- 表格标题 -->
			<template v-if="slots['title']" #title="currentPageData">
				<slot name="title" v-bind="currentPageData"></slot>
			</template>
		</a-table>

		<div
			v-if="pagination"
			ref="paginationRef"
			class="mt-md rounded-hg flex shrink-0 items-center bg-white"
			:class="{
				'justify-start': mergedPagination !== false && mergedPagination.position === 'left',
				'justify-center': mergedPagination !== false && mergedPagination.position === 'center',
				'justify-end': mergedPagination !== false && mergedPagination.position === 'right',
			}"
		>
			<a-pagination v-bind="mergedPagination" @change="handleChangePageSize" />
		</div>
	</div>
</template>

<style lang="scss" scoped>
.custom-table-container {
	/* 添加平滑过渡效果 */
	:deep(.ant-table-body) {
		transition: height 0.2s ease-in-out;
		// overflow: auto !important;
	}

	:deep(.ant-table) {
		transition: max-height 0.2s ease-in-out;
	}

	/* 初始化状态样式，减少闪烁 */
	:deep(.table-initializing) {
		opacity: 0.9;
	}
}
</style>
